import { useFormContext } from 'react-hook-form';
import { BareIconButton, IconName, Body, Stack } from '@optimus-web/core';
import { FormInputField, LocalizedText, useFeatureFlag, useLocalizeText, useSafeContext } from '@mews/framework';
import { AvailabilityBlockState } from '@mews-commander/data-access';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { SpacedForwardSlash } from '@mews-commander/ui';
import { WithAvailabilityBlockId } from '@mews-commander/types';
import { useCommanderSelector } from '@mews-commander/utils';
import { AvailabilityAdjustmentFormValues, ResourceCategoryField } from '../../../../../types';
import {
    getCurrentEnterpriseMinimalOperationEditableUtc,
    getReservationCapacityByResourceCategory,
    getResourceCategoryAdjustmentValue,
    getResourceCategoryPickedUpValue,
    isBlockAdjustmentReleased,
} from '../../../../../selectors';
import { InputWrapper } from '../styles';
import { OverrideReleaseDialogContext } from '../../../../OverrideAvailabilityReleaseDialog';
import { ADJUSTMENT_VALUE, AVAILABILITIES, FIELD_ARRAY_AVAILABILITY, VALUE } from '../../../../../constants';
import { useAvailabilityBlock } from '../../../../../utils/useAvailabilityBlock';

interface EditableCellProps extends WithAvailabilityBlockId {
    dateTimeUtc: string;
    disabled: boolean;
    rowIndex: number;
    columnIndex: number;
}

export const EditableCell: React.FC<EditableCellProps> = ({
    disabled,
    dateTimeUtc,
    rowIndex,
    columnIndex,
    availabilityBlockId,
}) => {
    const lt = useLocalizeText();
    const occupancyInBlocksEnabledStage2 = useFeatureFlag(FeatureFlag.occupancyInBlocksEnabledStage2);

    const { watch, getValues } = useFormContext<AvailabilityAdjustmentFormValues>();
    const field = watch(`${FIELD_ARRAY_AVAILABILITY}.${rowIndex}`) as ResourceCategoryField & { id: string };

    const { resourceCategoryId } = field;

    const blockState = useAvailabilityBlock(availabilityBlockId)?.state;

    const pickedUpValue = useCommanderSelector((state) =>
        getResourceCategoryPickedUpValue(state, { availabilityBlockId, dateTimeUtc, resourceCategoryId })
    );

    const reservationCapacity = useCommanderSelector((state) =>
        getReservationCapacityByResourceCategory(state, { resourceCategoryId, dateTimeUtc })
    );

    const blockedValue = useCommanderSelector((state) =>
        getResourceCategoryAdjustmentValue(state, { availabilityBlockId, dateTimeUtc, resourceCategoryId })
    );

    const isAdjustmentReleased = useCommanderSelector((state) =>
        isBlockAdjustmentReleased(state, { availabilityBlockId, dateTimeUtc, resourceCategoryId })
    );

    const minEditableUtc = useCommanderSelector(getCurrentEnterpriseMinimalOperationEditableUtc);

    const { openOverrideDialog } = useSafeContext(OverrideReleaseDialogContext);

    if (isAdjustmentReleased || (!blockedValue && dateTimeUtc < minEditableUtc)) {
        return (
            <Stack distribution="stretch" fill alignment="center" itemAlignment="center">
                <InputWrapper>
                    <Body variant="secondary">
                        <Stack spacing="200" itemAlignment="center">
                            <LocalizedText textKey={TextKey.Released} />
                            {minEditableUtc < dateTimeUtc && (
                                <BareIconButton
                                    icon={IconName.Reset}
                                    size="small"
                                    onClick={() =>
                                        openOverrideDialog({ dateTimeUtc, resourceCategoryId, isReleaseOverride: true })
                                    }
                                />
                            )}
                        </Stack>
                    </Body>
                </InputWrapper>
            </Stack>
        );
    }

    // sum of all filled values in form for the same space category
    const blockedSum = occupancyInBlocksEnabledStage2
        ? getValues().fieldArrayAvailability.reduce((acc, item, localRowIndex) => {
              const { value } = item.availabilities[columnIndex].adjustmentValue;
              const val = value ? Number(value) : null;
              return item.resourceCategoryId === resourceCategoryId && val && localRowIndex !== rowIndex
                  ? acc + val
                  : acc;
          }, 0)
        : 0;

    const maxSuffix =
        blockState === AvailabilityBlockState.Inquired ? reservationCapacity : reservationCapacity + blockedValue;

    const max = occupancyInBlocksEnabledStage2 ? Math.max(maxSuffix - blockedSum, 0) : maxSuffix;
    const min = pickedUpValue;

    const maxErrorMessage = occupancyInBlocksEnabledStage2
        ? lt({
              textKey: TextKey.AvailabilityBlockSpaceCategoryMaxExceededErrorMessage,
              substitutes: {
                  blockedSum: blockedSum + Number(field.availabilities[columnIndex].adjustmentValue.value),
                  available: maxSuffix,
              },
          })
        : lt({ textKey: TextKey.NumberPickerValidationErrorMessage, substitutes: { min, max } });

    return (
        <Stack distribution="stretch" fill alignment="center" itemAlignment="center">
            <InputWrapper>
                <FormInputField
                    inline
                    required
                    alignRight
                    type="number"
                    disabled={disabled}
                    min={min}
                    max={max}
                    name={`${FIELD_ARRAY_AVAILABILITY}.${rowIndex}.${AVAILABILITIES}.${columnIndex}.${ADJUSTMENT_VALUE}.${VALUE}`}
                    rules={{
                        required: {
                            value: true,
                            message: lt({ textKey: TextKey.ValidateRequired }),
                        },
                        pattern: {
                            value: /^\d+$/,
                            message: lt({ textKey: TextKey.ValidateNumber }),
                        },
                        min: {
                            value: min,
                            message: lt({
                                textKey: TextKey.NumberPickerValidationErrorMessage,
                                substitutes: { min, max },
                            }),
                        },
                        max: { value: max, message: maxErrorMessage },
                    }}
                    suffix={
                        <>
                            {SpacedForwardSlash}
                            {maxSuffix}
                        </>
                    }
                />
            </InputWrapper>
        </Stack>
    );
};
